/**
 * Color utility functions for theme migration to shadcn/ui standard
 * Converts RGB/Hex colors to HSL format used by shadcn/ui
 */

/**
 * Convert hex color to HSL
 * @param {string} hex - Hex color (e.g., "#475B63" or "475B63")
 * @returns {string} HSL color string (e.g., "240 5.9% 10%")
 */
export function hexToHsl(hex) {
  // Remove # if present
  hex = hex.replace("#", "");

  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        h = 0;
    }
  }

  // Convert to percentages and round
  h = Math.round(h * 360);
  s = Math.round(s * 100 * 10) / 10;
  l = Math.round(l * 100 * 10) / 10;

  return `${h} ${s}% ${l}%`;
}

/**
 * Convert RGB color to HSL
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} HSL color string
 */
export function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        h = 0;
    }
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100 * 10) / 10;
  l = Math.round(l * 100 * 10) / 10;

  return `${h} ${s}% ${l}%`;
}

/**
 * Convert rgba color to HSL (ignores alpha)
 * @param {string} rgba - RGBA color string (e.g., "rgba(71, 91, 99, 0.2)")
 * @returns {string} HSL color string
 */
export function rgbaToHsl(rgba) {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return "0 0% 0%";
  return rgbToHsl(parseInt(match[1]), parseInt(match[2]), parseInt(match[3]));
}

/**
 * Get HSL color with adjusted lightness
 * @param {string} hsl - HSL color string (e.g., "240 5.9% 10%")
 * @param {number} lightnessDelta - Lightness adjustment (-100 to 100)
 * @returns {string} Adjusted HSL color string
 */
export function adjustLightness(hsl, lightnessDelta) {
  const match = hsl.match(/(\d+)\s+([\d.]+)%\s+([\d.]+)%/);
  if (!match) return hsl;

  const h = match[1];
  const s = match[2];
  let l = parseFloat(match[3]) + lightnessDelta;
  l = Math.max(0, Math.min(100, l));

  return `${h} ${s}% ${l}%`;
}
